import { useState, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Download, Upload, FileSpreadsheet, Users, Pill, Activity, Calendar, AlertTriangle, CheckCircle, Loader2, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const exportOptions = [
  { key: 'ospiti', label: 'Ospiti', icon: Users, desc: 'Anagrafica completa di tutti gli ospiti', color: 'blue' },
  { key: 'terapie', label: 'Terapie', icon: Pill, desc: 'Tutte le terapie con scorte e prescrittori', color: 'emerald' },
  { key: 'parametri-vitali', label: 'Parametri Vitali', icon: Activity, desc: 'PA, FC, SpO2, temperatura, glicemia', color: 'violet' },
  { key: 'appuntamenti', label: 'Appuntamenti', icon: Calendar, desc: 'Visite programmate ed effettuate', color: 'amber' },
  { key: 'eventi-avversi', label: 'Eventi Avversi', icon: AlertTriangle, desc: 'Cadute, lesioni, agitazione e altri eventi', color: 'red' },
];

export default function CsvManager() {
  const [exporting, setExporting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const handleExport = async (key) => {
    setExporting(key);
    try {
      const token = localStorage.getItem('rsa_token');
      const response = await fetch(`${BACKEND}/api/csv/export/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Errore export');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('content-disposition');
      const filename = disposition?.match(/filename=(.+)/)?.[1] || `${key}.csv`;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${key} completato`);
    } catch {
      toast.error('Errore durante l\'export');
    }
    setExporting(null);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('rsa_token');
      const res = await fetch(`${BACKEND}/api/csv/import/ospiti`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore import');
      setImportResult(data);
      if (data.imported > 0) toast.success(`${data.imported} ospiti importati`);
      if (data.skipped > 0) toast.info(`${data.skipped} righe saltate`);
    } catch (err) {
      toast.error(err.message || 'Errore durante l\'import');
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    red: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="csv-manager-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-brand" />
          Import / Export CSV
        </h1>
        <p className="text-sm text-slate-500 mt-1">Esporta dati in formato CSV per Excel o importa ospiti da file</p>
      </div>

      {/* EXPORT SECTION */}
      <Card className="border-0 shadow-md" data-testid="csv-export-section">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="w-4 h-4 text-brand" />Export Dati
          </CardTitle>
          <CardDescription>Scarica i dati in formato CSV compatibile con Excel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {exportOptions.map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  onClick={() => handleExport(opt.key)}
                  disabled={exporting !== null}
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${colorMap[opt.color]} disabled:opacity-50`}
                  data-testid={`csv-export-${opt.key}`}
                >
                  {exporting === opt.key ? (
                    <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
                  ) : (
                    <Icon className="w-5 h-5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{opt.label}</p>
                    <p className="text-[10px] opacity-70">{opt.desc}</p>
                  </div>
                  <Download className="w-4 h-4 opacity-40 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* IMPORT SECTION */}
      <Card className="border-0 shadow-md" data-testid="csv-import-section">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-4 h-4 text-emerald-600" />Import Ospiti
          </CardTitle>
          <CardDescription>Carica un file CSV con i dati degli ospiti da importare</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-dashed border-slate-300">
            <p className="text-xs text-slate-500 mb-3">
              Il file CSV deve avere le colonne: <span className="font-mono text-slate-700">cognome, nome, codice_fiscale, data_nascita, sesso, telefono, email, data_ingresso, provenienza, stanza, letto, tipologia_retta, medico_base, note</span>.
              Le colonne <span className="font-semibold">cognome</span> e <span className="font-semibold">nome</span> sono obbligatorie.
            </p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} data-testid="csv-import-input" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="csv-import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? 'Importazione...' : 'Seleziona File CSV'}
            </Button>
          </div>

          {/* Import result */}
          {importResult && (
            <div className="bg-white rounded-xl border p-4 space-y-2" data-testid="csv-import-result">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-semibold text-emerald-700">{importResult.imported} importati</span>
                </div>
                {importResult.skipped > 0 && (
                  <div className="flex items-center gap-2">
                    <FileWarning className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold text-amber-700">{importResult.skipped} saltati</span>
                  </div>
                )}
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-[10px] text-amber-600">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
